<script setup lang="ts">
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import SiteFooter from '@/components/SiteFooter.vue'
import SiteHeader from '@/components/SiteHeader.vue'
import { NOVA_AI_URL } from '@/config/external-links'
import { authSession } from '@/services/auth'

const router = useRouter()
const loggingOut = ref(false)
const avatarFailed = ref(false)
const profile = computed(() => authSession.user.value)
const displayName = computed(() => profile.value?.nickname?.trim() || 'Nova 用户')
const initial = computed(() => displayName.value.slice(0, 1).toUpperCase())
const avatarUrl = computed(() => {
  const avatar = profile.value?.avatar?.trim()
  if (!avatar || !/^(https?:\/\/|\/\/|\/|data:image\/)/i.test(avatar)) return undefined
  return avatar
})
const isCertified = computed(() => profile.value?.certificationStatus === 1)

const maskMobile = (mobile?: string) => mobile?.replace(/^(\d{3})\d{4}(\d{4})$/, '$1****$2') || '未绑定手机号'
const maskEmail = (email?: string) => {
  if (!email?.includes('@')) return '未绑定邮箱'
  const [name, domain] = email.split('@')
  return `${name.slice(0, 2)}***@${domain}`
}

const logout = async () => {
  loggingOut.value = true
  await authSession.logout()
  await router.replace('/')
}
</script>

<template>
  <div class="account-page">
    <SiteHeader />
    <main class="account-main">
      <div class="account-shell shell">
        <section class="account-profile-card" aria-labelledby="account-title">
          <header class="account-heading">
            <div class="account-avatar" :class="{ 'has-image': avatarUrl && !avatarFailed }">
              <img
                v-if="avatarUrl && !avatarFailed"
                :src="avatarUrl"
                alt=""
                @error="avatarFailed = true"
              />
              <span v-else aria-hidden="true">{{ initial }}</span>
            </div>
            <div class="account-identity">
              <h1 id="account-title">{{ displayName }}</h1>
              <p>你的 Nova 会员账号</p>
            </div>
            <span class="account-certification" :class="{ verified: isCertified }">
              {{ isCertified ? '已认证' : '认证审核中' }}
            </span>
          </header>

          <dl class="account-details" aria-label="账号信息">
            <div><dt>手机号</dt><dd>{{ maskMobile(profile?.mobile) }}</dd></div>
            <div><dt>邮箱</dt><dd>{{ maskEmail(profile?.email) }}</dd></div>
            <div><dt>认证状态</dt><dd>{{ isCertified ? '已完成身份认证' : '未认证或审核中' }}</dd></div>
          </dl>
        </section>

        <aside class="account-creation-card" aria-labelledby="creation-card-title">
          <div>
            <h2 id="creation-card-title">继续创作</h2>
            <p>前往 Nova AI 创作平台，继续管理你的作品与生成任务。</p>
          </div>
          <div class="account-actions">
            <a class="primary-action" :href="NOVA_AI_URL" target="_blank" rel="noopener">进入 AI 创作平台</a>
            <button class="ghost-action" type="button" :disabled="loggingOut" @click="logout">{{ loggingOut ? '正在退出…' : '退出登录' }}</button>
          </div>
        </aside>
      </div>
    </main>
    <SiteFooter />
  </div>
</template>
